import { useState, useEffect } from 'react';
import logger from '../lib/logger';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  getReportPreferences, setReportPreferences,
  sendReportNow, previewReport
} from '../lib/api';
import { toast } from 'sonner';
import {
  FileText, Mail, Send, Loader2, Calendar, Clock,
  TrendingUp, PieChart, CheckCircle2
} from 'lucide-react';
import { motion } from 'framer-motion';

const Reports = () => {
  const [preferences, setPreferences] = useState({
    weekly_report: false,
    monthly_report: false,
    report_day: 1,
    include_pdf: false,
    email: ''
  });
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    fetchPreferences();
  }, []);

  const fetchPreferences = async () => {
    try {
      const { data } = await getReportPreferences();
      setPreferences(data);
    } catch (error) {
      logger.error('Error fetching preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePreferences = async () => {
    setSaving(true);
    try {
      await setReportPreferences(preferences);
      toast.success('Report preferences saved');
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleSendNow = async (period) => {
    setSending(true);
    try {
      const { data } = await sendReportNow(period);
      toast.success(data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send report');
    } finally {
      setSending(false);
    }
  };

  const handlePreview = async (period) => {
    setPreviewLoading(true);
    try {
      const { data } = await previewReport(period);
      setPreview(data);
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <motion.div
        className="space-y-6 max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="reports-container"
      >
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Spending Reports</h1>
          <p className="text-muted-foreground">Automated email summaries of your spending</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Report Preferences */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Mail className="w-5 h-5 text-emerald-500" />
                Email Reports
              </CardTitle>
              <CardDescription>Configure automated spending summaries</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Weekly Report */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/10">
                    <Calendar className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="font-medium">Weekly Report</p>
                    <p className="text-sm text-muted-foreground">Every Monday morning</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.weekly_report}
                  onCheckedChange={(v) => setPreferences({ ...preferences, weekly_report: v })}
                  data-testid="weekly-report-toggle"
                />
              </div>

              {/* Monthly Report */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Calendar className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="font-medium">Monthly Report</p>
                    <p className="text-sm text-muted-foreground">1st of every month</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.monthly_report}
                  onCheckedChange={(v) => setPreferences({ ...preferences, monthly_report: v })}
                  data-testid="monthly-report-toggle"
                />
              </div>

              {/* PDF Option */}
              <div className="flex items-center justify-between p-4 rounded-xl bg-muted">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-orange-500/10">
                    <FileText className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <p className="font-medium">Include PDF Attachment</p>
                    <p className="text-sm text-muted-foreground">Get a downloadable report</p>
                  </div>
                </div>
                <Switch
                  checked={preferences.include_pdf}
                  onCheckedChange={(v) => setPreferences({ ...preferences, include_pdf: v })}
                  data-testid="pdf-toggle"
                />
              </div>

              <Button
                onClick={handleSavePreferences}
                disabled={saving}
                className="w-full rounded-xl"
                data-testid="save-preferences-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Save Preferences
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Send Now */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Send className="w-5 h-5 text-emerald-500" />
                Send Report Now
              </CardTitle>
              <CardDescription>Get an immediate spending summary</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <Button
                  variant="outline"
                  onClick={() => handleSendNow('weekly')}
                  disabled={sending}
                  className="h-20 rounded-xl flex-col"
                  data-testid="send-weekly-btn"
                >
                  <Calendar className="w-6 h-6 mb-2" />
                  <span>Weekly Summary</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSendNow('monthly')}
                  disabled={sending}
                  className="h-20 rounded-xl flex-col"
                  data-testid="send-monthly-btn"
                >
                  <Calendar className="w-6 h-6 mb-2" />
                  <span>Monthly Summary</span>
                </Button>
              </div>

              <div className="border-t pt-4">
                <Label className="text-sm text-muted-foreground mb-3 block">Preview Report</Label>
                <div className="grid grid-cols-2 gap-4">
                  <Button
                    variant="ghost"
                    onClick={() => handlePreview('weekly')}
                    disabled={previewLoading}
                    className="rounded-xl"
                  >
                    Preview Weekly
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => handlePreview('monthly')}
                    disabled={previewLoading}
                    className="rounded-xl"
                  >
                    Preview Monthly
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Report Preview */}
        {preview && (
          <Card data-testid="report-preview">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
                Report Preview ({preview.period})
              </CardTitle>
              <CardDescription>
                {new Date(preview.start_date).toLocaleDateString()} - {new Date(preview.end_date).toLocaleDateString()}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="p-4 rounded-xl bg-muted text-center">
                  <p className="text-sm text-muted-foreground">Total Spent</p>
                  <p className="text-2xl font-bold text-emerald-500">${preview.total_spent?.toFixed(2)}</p>
                </div>
                <div className="p-4 rounded-xl bg-muted text-center">
                  <p className="text-sm text-muted-foreground">Transactions</p>
                  <p className="text-2xl font-bold">{preview.transaction_count}</p>
                </div>
                <div className="p-4 rounded-xl bg-muted text-center">
                  <p className="text-sm text-muted-foreground">Avg Transaction</p>
                  <p className="text-2xl font-bold">${preview.average_transaction?.toFixed(2)}</p>
                </div>
                <div className="p-4 rounded-xl bg-muted text-center">
                  <p className="text-sm text-muted-foreground">Categories</p>
                  <p className="text-2xl font-bold">{preview.top_categories?.length || 0}</p>
                </div>
              </div>

              {preview.top_categories?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <PieChart className="w-4 h-4" />
                    Top Categories
                  </h4>
                  <div className="space-y-2">
                    {preview.top_categories.map((cat, idx) => (
                      <div key={cat.category} className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center gap-2">
                          <span className="w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-600 flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </span>
                          <span>{cat.category}</span>
                        </div>
                        <span className="font-mono font-semibold">${cat.amount?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Info Card */}
        <Card className="bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border-emerald-500/20">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-emerald-500/20">
                <Clock className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-heading font-semibold mb-1">Report Delivery Schedule</h3>
                <p className="text-sm text-muted-foreground">
                  Weekly reports are sent every Monday at 8:00 AM UTC.<br />
                  Monthly reports are sent on the 1st of each month at 8:00 AM UTC.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </Layout>
  );
};

export default Reports;
